// Calendar-date helpers for timesheets — NO browser time-zone logic.
//
// Timesheet dates are calendar days ('YYYY-MM-DD'). They are never converted to
// the browser's local time: arithmetic and weekday/label formatting are done in
// UTC on the calendar date itself, so every user sees the same days regardless of
// where their computer is. "Today" is NOT computed here — it comes from the web
// server (timesheet.ServerToday).

const pad = (n) => String(n).padStart(2, '0');

// Strip any value to its 'YYYY-MM-DD' calendar date ('' when missing).
export function toDateOnly(v) {
  if (v == null || v === '') return '';
  return String(v).slice(0, 10);
}

// 'YYYY-MM-DD' -> Date at UTC midnight (only for UTC getters / UTC formatting).
function utcDate(ymd) {
  const [y, m, d] = toDateOnly(ymd).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

// Add n calendar days to a 'YYYY-MM-DD' date.
export function addDays(ymd, n) {
  const d = utcDate(ymd);
  d.setUTCDate(d.getUTCDate() + n);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

// Every calendar date from start to end inclusive, as 'YYYY-MM-DD' strings.
export function datesInRange(start, end) {
  const s = toDateOnly(start);
  const e = toDateOnly(end);
  const out = [];
  if (!s || !e) return out;
  for (let d = s; d <= e; d = addDays(d, 1)) out.push(d); // YYYY-MM-DD compares as text
  return out;
}

// Day of week for a calendar date: 0 = Sunday ... 6 = Saturday.
export function weekday(ymd) {
  return utcDate(ymd).getUTCDay();
}

// Format a calendar date for display (e.g. { month: 'short', day: 'numeric' }).
export function formatDate(ymd, options) {
  return utcDate(ymd).toLocaleDateString('en-US', { ...options, timeZone: 'UTC' });
}

// The app's standard date display: MM/DD/YYYY (e.g. 10/21/2026). Accepts a
// 'YYYY-MM-DD' date or an ISO date-time (only the calendar date is used).
// ('' when missing.) { weekday: true } prefixes the day ("Wed 10/21/2026") for
// day-by-day screens. The tabular timesheet keeps its own weekday + m/d labels.
export function displayDate(v, { weekday: withDay = false } = {}) {
  const ymd = toDateOnly(v);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return v ? String(v) : '';
  const [y, m, d] = ymd.split('-');
  const text = `${m}/${d}/${y}`;
  return withDay ? `${formatDate(ymd, { weekday: 'short' })} ${text}` : text;
}

// A date range (e.g. a timesheet period): "10/11/2026 – 10/24/2026".
export function displayRange(start, end) {
  return `${displayDate(start)} – ${displayDate(end)}`;
}

// The same range shortened for small screens: "10/16 – 10/31/26" (the year
// once, 2 digits), or "12/16/25 – 01/15/26" when the years differ.
export function displayRangeShort(start, end) {
  const a = toDateOnly(start);
  const b = toDateOnly(end);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(a) || !/^\d{4}-\d{2}-\d{2}$/.test(b)) return displayRange(start, end);
  const md = (ymd) => `${ymd.slice(5, 7)}/${ymd.slice(8, 10)}`;
  const yy = (ymd) => ymd.slice(2, 4);
  return a.slice(0, 4) === b.slice(0, 4)
    ? `${md(a)} – ${md(b)}/${yy(b)}`
    : `${md(a)}/${yy(a)} – ${md(b)}/${yy(b)}`;
}

// Month label for a 'YYYY-MM-..' date, e.g. "Oct 2026".
export function displayMonth(v) {
  const ymd = toDateOnly(v);
  return ymd ? formatDate(`${ymd.slice(0, 7)}-01`, { month: 'short', year: 'numeric' }) : '';
}
